<template>
  <BaseDialog
    v-model="showDialog"
    :title="dialogTitle"
    :subtitle="dialogSubtitle"
    width="600px"
    :persistent="isGenerating"
    :hide-close-button="isGenerating"
    :show-footer="true"
    :show-cancel="!isGenerating && !downloadLinkGenerated"
    :show-confirm="true"
    :confirm-label="confirmButtonLabel"
    :loading="isGenerating"
    :disabled="isGenerating || (!downloadLinkGenerated && !canGenerate)"
    button-direction="vertical"
    :progress-percentage="progressPercentage"
    :progress-step="currentStep"
    :confirm-icon="confirmButtonIcon"
    @confirm="handleConfirm"
    @cancel="handleCancel"
    @hide="handleDialogHide"
    class="share-collection-dialog"
  >
    <div class="share-content">
      <!-- Initial State: Show collection info and generate button -->
      <div v-if="!isGenerating && !downloadLinkGenerated" class="generate-section">
        <div class="download-icon">
          <div class="download-icon-wrapper">
            <q-icon name="o_file_download" size="34px" color="positive" />
          </div>
        </div>
        <div class="generate-title">Generate Download Link</div>
        <div class="generate-subtitle">{{ shareDescription }}</div>
      </div>

      <!-- Generation Progress State -->
      <div v-if="isGenerating" class="progress-section">
        <div class="download-icon">
          <q-spinner-pie color="primary" size="64px" />
        </div>
        <div class="progress-title">Creating Download Link...</div>
        <div class="progress-subtitle">Please wait while we prepare your collection</div>

        <div class="progress-details">
          <q-linear-progress :value="progressPercentage / 100" color="primary" size="8px" rounded class="progress-bar" />
          <div class="progress-text">{{ progressPercentage }}%</div>
        </div>

        <div v-if="currentStep" class="current-step">
          {{ currentStep }}
        </div>
      </div>

      <!-- Success State: Show generated link -->
      <div v-if="downloadLinkGenerated" class="success-section">
        <div class="download-icon">
          <q-icon name="o_check_circle" size="64px" color="positive" />
        </div>
        <div class="success-title">Complete!</div>
        <div class="success-subtitle">{{ shareDescription }}</div>

        <div class="link-container">
          <q-input v-model="shareableLink" readonly outlined class="link-input">
            <template v-slot:append>
              <q-btn flat round icon="content_copy" @click="copyLink" color="primary" />
            </template>
          </q-input>
        </div>

        <div class="expiry-info">
          <q-icon name="o_info" size="20px" />
          <span>This link will expire on {{ formatExpiryDate(expiryDate) }}</span>
        </div>
      </div>
    </div>
  </BaseDialog>
</template>

<script>
import BaseDialog from '@/components/base/BaseDialog.vue';
import { useMutation, useQueryClient } from '@tanstack/vue-query';

export default {
  name: 'ShareContentDialog',

  components: {
    BaseDialog
  },

  props: {
    show: {
      type: Boolean,
      default: false
    },
    collectionId: {
      type: [String, Number],
      default: null
    },
    collectionName: {
      type: String,
      default: ''
    },
    contentIds: {
      type: Array,
      default: () => []
    },
    shareType: {
      type: String,
      default: 'collection', // 'collection' or 'content'
      validator: value => ['collection', 'content'].includes(value)
    }
  },

  emits: ['update:show', 'link-generated', 'error'],

  data() {
    return {
      showDialog: false,
      isGenerating: false,
      downloadLinkGenerated: false,
      shareId: null,
      downloadUrl: null,
      shareableLink: '',
      expiryDate: null,
      progressPercentage: 0,
      currentStep: '',
      pollInterval: null
    };
  },

  computed: {
    canGenerate() {
      return this.contentIds && this.contentIds.length > 0;
    },

    dialogTitle() {
      return this.shareType === 'collection' ? 'Share Collection' : 'Share Files';
    },

    dialogSubtitle() {
      if (this.shareType === 'collection') {
        return this.collectionName;
      } else {
        const count = this.contentIds.length;
        return `${count} selected file${count !== 1 ? 's' : ''}`;
      }
    },

    confirmButtonLabel() {
      if (this.isGenerating) {
        return 'Generating...';
      }
      if (this.downloadLinkGenerated) {
        return 'Done';
      }
      return 'Generate download link';
    },

    confirmButtonIcon() {
      if (this.isGenerating) {
        return ''; // No icon when loading spinner is shown
      }
      if (this.downloadLinkGenerated) {
        return '';
      }
      return 'o_link';
    },

    contentSummary() {
      if (!this.contentIds || this.contentIds.length === 0) return null;

      // Handle both content objects (from ImagesGrid) and content IDs (from collections)
      const images = this.contentIds.filter(item => {
        // If item is an object, check its type property
        if (typeof item === 'object' && item !== null) {
          return item.type !== 'video';
        }
        // If item is just an ID, assume it's an image (fallback)
        return true;
      }).length;

      const videos = this.contentIds.filter(item => {
        // If item is an object, check its type property
        if (typeof item === 'object' && item !== null) {
          return item.type === 'video';
        }
        // If item is just an ID, assume it's not a video (fallback)
        return false;
      }).length;

      return {
        total: this.contentIds.length,
        images,
        videos
      };
    },

    shareDescription() {
      if (this.shareType === 'collection') {
        return 'Create a download link that anyone can use to access your selected assets of this collection.';
      } else {
        return 'Create a download link that anyone can use to access your selected assets.';
      }
    }
  },

  watch: {
    show: {
      immediate: true,
      handler(newVal) {
        this.showDialog = newVal;
      }
    },

    showDialog(newVal) {
      this.$emit('update:show', newVal);
    }
  },

  setup() {
    const queryClient = useQueryClient();

    // Create mutation for enqueuing share job
    const enqueueShareJobMutation = useMutation({
      mutationFn: async ({ contentIds, message }) => {
        const { ShareApi } = require('@api/modules/share');
        return ShareApi.enqueueShareJob(contentIds, message);
      }
    });

    // Create mutation for checking share job status
    const checkShareStatusMutation = useMutation({
      mutationFn: async shareId => {
        const { ShareApi } = require('@api/modules/share');
        return ShareApi.getShareJobStatus(shareId);
      }
    });

    return {
      enqueueShareJobMutation,
      checkShareStatusMutation,
      queryClient
    };
  },

  methods: {
    async handleConfirm() {
      if (this.downloadLinkGenerated) {
        this.handleClose();
        return;
      }

      if (!this.canGenerate) return;

      try {
        await this.generateDownloadLink();
      } catch (error) {
        console.error('Error generating download link:', error);
        this.$emit('error', error.message || 'Failed to generate download link');
      }
    },

    handleCancel() {
      this.handleClose();
    },

    handleDialogHide() {
      this.resetDialog();
    },

    handleClose() {
      this.showDialog = false;
    },

    async generateDownloadLink() {
      this.isGenerating = true;
      this.progressPercentage = 0;
      this.currentStep = 'Preparing assets...';

      try {
        // Prepare content data with proper format
        const contentData = this.contentIds.map(item => {
          // If item is already an object with id and type, use it directly
          if (typeof item === 'object' && item !== null && item.id) {
            return {
              id: item.id,
              type: item.type || 'image'
            };
          }
          // If item is just an ID, assume it's an image
          return {
            id: item,
            type: 'image'
          };
        });

        // Create custom message for the share
        const message =
          this.shareType === 'collection'
            ? `Collection "${this.collectionName}" shared from Flashy`
            : `${this.contentIds.length} selected file${this.contentIds.length !== 1 ? 's' : ''} shared from Flashy`;

        // Start share job
        const response = await this.enqueueShareJobMutation.mutateAsync({
          contentIds: contentData,
          message: message
        });

        if (response.data?.shareId) {
          this.shareId = response.data.shareId;

          // Check if already completed (for small collections)
          if (response.data.status === 'completed' && response.data.downloadUrl) {
            this.handleGenerationComplete(response.data);
          } else {
            // Start polling for progress
            await this.pollProgress(response.data.shareId);
          }
        }
      } catch (error) {
        this.isGenerating = false;
        throw error;
      }
    },

    async pollProgress(shareId) {
      this.pollInterval = setInterval(async () => {
        try {
          const response = await this.checkShareStatusMutation.mutateAsync(shareId);
          const { status, progress, message, downloadUrl, isExpired } = response.data;

          this.progressPercentage = Math.min(progress || 0, 100);

          // Update current step based on status and message
          if (message) {
            this.currentStep = message;
          } else {
            // Fallback status messages
            switch (status) {
              case 'pending':
                this.currentStep = 'Queued for processing...';
                break;
              case 'processing':
                this.currentStep = 'Processing files...';
                break;
              case 'completed':
                this.currentStep = 'Complete!';
                break;
              case 'failed':
                this.currentStep = 'Processing failed';
                break;
              default:
                this.currentStep = 'Processing...';
            }
          }

          if (status === 'completed' && downloadUrl) {
            this.handleGenerationComplete(response.data);
          } else if (status === 'failed' || isExpired) {
            throw new Error(message || 'Share job failed or expired');
          }
        } catch (error) {
          this.stopPolling();
          this.isGenerating = false;
          throw error;
        }
      }, 2000); // Poll every 2 seconds
    },

    handleGenerationComplete(data) {
      this.stopPolling();
      this.isGenerating = false;
      this.downloadLinkGenerated = true;
      this.downloadUrl = data.downloadUrl;
      this.expiryDate = data.expiresAt;

      // Clear the current step to prevent text bleeding into other UI elements
      this.currentStep = '';

      // Use the brandUrl from the API response if available, otherwise fallback to manual construction
      if (data.brandUrl) {
        // Check if brandUrl is a complete URL (starts with http)
        if (data.brandUrl.startsWith('http')) {
          this.shareableLink = data.brandUrl;
        } else {
          // Extract the d-{shareId} part from the brandUrl (remove any prefix like "abcxyz/")
          const downloadPath = data.brandUrl.includes('/d-') ? data.brandUrl.substring(data.brandUrl.indexOf('/d-')) : `/d-${this.shareId}`;
          this.shareableLink = `${window.location.origin}${downloadPath}`;
        }
      } else {
        // Fallback to the old method if brandUrl is not provided
        this.shareableLink = `${window.location.origin}/d-${this.shareId}`;
      }

      this.$emit('link-generated', {
        shareId: this.shareId,
        downloadUrl: data.downloadUrl,
        shareableLink: this.shareableLink,
        expiryDate: data.expiresAt
      });

      this.$amplitude.trackEvent('Share link created', {
        shareId: this.shareId,
        downloadUrl: this.downloadUrl,
        shareableLink: this.shareableLink
      });
    },

    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },

    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.shareableLink);
        this.$q.notify({
          type: 'positive',
          message: 'Link copied to clipboard',
          position: 'top',
          timeout: 2000
        });
      } catch (error) {
        console.error('Error copying to clipboard:', error);
        this.$q.notify({
          type: 'negative',
          message: 'Failed to copy link',
          position: 'top',
          timeout: 2000
        });
      }
    },

    formatExpiryDate(date) {
      if (!date) return '';
      // Use the existing formatDate utility and enhance with better formatting
      const d = new Date(date);
      return d.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    },

    resetDialog() {
      this.isGenerating = false;
      this.downloadLinkGenerated = false;
      this.shareId = null;
      this.downloadUrl = null;
      this.shareableLink = '';
      this.expiryDate = null;
      this.progressPercentage = 0;
      this.currentStep = '';
      this.stopPolling();
    }
  },

  beforeDestroy() {
    this.stopPolling();
  }
};
</script>

<style lang="scss" scoped>
.share-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
}

.download-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
}

.download-icon-wrapper {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: var(--light-positive);
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
}

// Generate Section Styles
.generate-section {
  width: 100%;

  .generate-title {
    @include mona-sans-font($size: 22px, $bolded: true, $color: var(--main-text-color));
    margin-bottom: 16px;
  }

  .generate-subtitle {
    @include inter-font($size: 16px, $color: var(--secondary-text-color));
    line-height: 1.5;
  }

  .content-summary {
    background-color: var(--terziary-btn-color);
    border-radius: 12px;
    padding: 20px;
    margin-top: 20px;

    .summary-title {
      @include mona-sans-font($size: 16px, $bolded: true, $color: var(--main-text-color));
      margin-bottom: 12px;
    }

    .content-breakdown {
      display: flex;
      gap: 8px;
      justify-content: center;
    }

    .content-chip {
      @include inter-font($size: 12px, $bolded: true, $color: var(--main-text-color));

      &.image-chip {
        background-color: var(--image-chip-bg);
        color: var(--image-chip-text);
      }

      &.video-chip {
        background-color: var(--video-chip-bg);
        color: var(--video-chip-text);
      }
    }
  }
}

// Progress Section Styles
.progress-section {
  width: 100%;

  .progress-title {
    @include mona-sans-font($size: 22px, $bolded: true, $color: var(--main-text-color));
    margin-bottom: 8px;
  }

  .progress-subtitle {
    @include inter-font($size: 16px, $color: var(--secondary-text-color));
    margin-bottom: 32px;
  }

  .progress-details {
    width: 100%;
    margin-bottom: 16px;

    .progress-bar {
      margin-bottom: 8px;
    }

    .progress-text {
      @include inter-font($size: 16px, $bolded: true, $color: var(--main-text-color));
    }
  }

  .current-step {
    @include inter-font($size: 14px, $color: var(--secondary-text-color));
    font-style: italic;
  }
}

// Success Section Styles
.success-section {
  width: 100%;

  .success-title {
    @include mona-sans-font($size: 22px, $bolded: true, $color: var(--main-text-color));
    margin-bottom: 8px;
  }

  .success-subtitle {
    @include inter-font($size: 16px, $color: var(--secondary-text-color));
    margin-bottom: 24px;
    line-height: 1.5;
  }

  .link-container {
    width: 100%;
    margin-bottom: 16px;

    .link-input {
      :deep(.q-field__control) {
        background-color: var(--terziary-btn-color);
      }

      :deep(.q-field__native) {
        @include inter-font($size: 14px, $color: var(--main-text-color));
      }
    }
  }

  .expiry-info {
    display: flex;
    height: 56px;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    @include inter-font($size: 14px, $color: var(--main-text-color));
    background-color: var(--terziary-btn-color);
    padding: 12px;
    border-radius: 8px;

    .q-icon {
      color: var(--main-text-color);
    }
  }
}
</style>
